import React from 'react'

import { SegmentSplitChart } from '../components/cut/SegmentSplitChart'
import { ChoiceBarsPlot, readChoiceSlide } from '../plots/choice-bars'
import { EditableSlideText } from './EditableSlideText'

import type { SegmentRow, SegmentSplit } from '@digital-twins/slide-schemas/generated/slide-types'
import type { StoredChoiceSlide } from '../plots/choice-bars'
import type { EditableSlideProps } from './contracts'
import type { ResolvedChartSegmentDefault } from '@digital-twins/shared-types'
import { ChartHeadingGap, ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'

/**
 * The chart of a single/multi-select bars slide (plots plan J): the one place
 * those two slides decide what they draw.
 *
 * - `renderCutSplit`: a slide split by one computed cut draws its groups in
 *   place of the bars. This is the ONE routing seam to the cut split chart:
 *   today it draws `SegmentSplitChart`; step Q moves the split chart to the
 *   `heat-grid` plot by changing this function's body only.
 * - `ChoiceSlideBars`: every other slide draws the `choice-bars` plot, from
 *   the slide's stamped `plot`, or from its stored `bars` through the legacy
 *   read adapter (decks stored before J). The renderer never re-ranks,
 *   re-rounds or crowns.
 */

export type ChoiceSlideType = 'single-select-bars' | 'multi-select-bars'

export interface CutSplitArgs {
  slideType: ChoiceSlideType
  chartTitle: string
  rows: ReadonlyArray<SegmentRow>
  split: SegmentSplit
}

/** The cut split chart of a bars slide (the seam step Q re-routes to `heat-grid`). */
export function renderCutSplit(args: CutSplitArgs) {
  return <SegmentSplitChart slideType={args.slideType} chartTitle={args.chartTitle} rows={args.rows} split={args.split} />
}

export interface ChoiceSlideBarsProps extends Pick<EditableSlideProps, 'editable' | 'onEdit' | 'renderMode' | 'plotOtherToggleLabels' | 'plotOtherMembersHeading'> {
  slide: StoredChoiceSlide
  /** Row test ids: `single-select` or `multi-select`, as the slides always had. */
  testIdPrefix: 'single-select' | 'multi-select'
  locale?: string | null
}

/** The stored Other hover list's title when the host passes no words of its own (legacy reader only). */
const LEGACY_OTHER_MEMBERS_HEADING = 'Includes:'

export function ChoiceSlideBars(props: ChoiceSlideBarsProps) {
  const { slide, testIdPrefix, locale, editable, onEdit, renderMode, plotOtherToggleLabels, plotOtherMembersHeading } = props

  const { payload, isStamped, formatShare } = readChoiceSlide(slide)
  const fieldPrefix = isStamped ? 'plot.data.series.0.items' : 'bars'

  return (
    <ChoiceBarsPlot
      data={payload.data}
      size="slide"
      renderMode={renderMode}
      locale={locale}
      formatShare={formatShare}
      testIdPrefix={testIdPrefix}
      // A stored slide titles its Other hover list with the host's word
      // (`studyReport.plotOther.includes`), else today's heading for hosts
      // without words; a stamped plot lists the members only.
      otherMembersHeading={isStamped ? null : plotOtherMembersHeading ?? LEGACY_OTHER_MEMBERS_HEADING}
      otherToggleLabels={plotOtherToggleLabels}
      renderLabel={({ item, index, maxLines, style, chartLabel }) => (
        <EditableSlideText
          data-testid={`${testIdPrefix}-bar-label`}
          value={item.label}
          field={`${fieldPrefix}.${index}.label`}
          editable={editable}
          onEdit={onEdit}
          minLines={1}
          maxLines={maxLines}
          lineHeight="1.2"
          tooltipOnTruncate
          chartLabel={chartLabel}
          renderMode={renderMode}
          style={style}
        />
      )}
    />
  )
}

/** The same native choice body, with recorded group meaning and usable base.
 * The recorded payload is immutable; edits still belong to the retained slide.
 */
export function RecordedChoiceSlideBars(props: {
  source: ResolvedChartSegmentDefault
  title: React.ReactNode
  testIdPrefix: 'single-select' | 'multi-select'
  renderMode?: ChoiceSlideBarsProps['renderMode']
  plotOtherToggleLabels?: ChoiceSlideBarsProps['plotOtherToggleLabels']
  multiSelectNote?: string
}) {
  const { source, title, testIdPrefix, renderMode, plotOtherToggleLabels, multiSelectNote } = props
  const group = source.groupContext
  return (
    <div data-testid="chart-segment-recorded-choice" style={{ height: '100%', padding: '40px 32px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <ChartSegmentTitleSlot contentPadRight={32} style={{ flexShrink: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--slide-text-primary)', lineHeight: 1.2 }}>{title}</div>
      </ChartSegmentTitleSlot>
      <div data-testid="chart-segment-recorded-context" style={{ marginTop: 8, fontSize: 13, lineHeight: 1.4, color: 'var(--slide-text-muted)', flexShrink: 0, overflowWrap: 'anywhere' }}>
        <div>{source.scopeLabel}: {group.label}</div>
        <div>{group.conditions.map((condition) => `${condition.sourceColumn} = ${condition.values.join(' / ')}`).join(' · ')}</div>
        {group.description ? <div>{group.description}</div> : null}
        <div>{source.baseLabel}</div>
      </div>
      <ChartHeadingGap height={source.columnIds.length > 5 ? 28 : 40} minHeight={12} />
      <ChoiceSlideBars
        slide={{ type: testIdPrefix === 'single-select' ? 'single-select-bars' : 'multi-select-bars', plot: source.plot, bars: [] }}
        testIdPrefix={testIdPrefix}
        locale={source.locale}
        renderMode={renderMode}
        plotOtherToggleLabels={plotOtherToggleLabels}
      />
      {multiSelectNote ? <div style={{ marginTop: 16, fontSize: 12, color: 'var(--slide-text-muted)', fontStyle: 'italic', flexShrink: 0 }}>{multiSelectNote}</div> : null}
    </div>
  )
}
